import useAppStore from "@/context/store";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import { Button, Dropdown } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Else, If, Then, When } from "react-if";
import {
  followUser,
  getFollowerUsers,
  toBlackList,
  unfollowUser,
} from "../../services";
import displayNum from "../../utils/displayNum";
import imageUrl from "../../utils/imageUrl";
import HBButton from "../HBButton";
import HBIcon from "../HBIcon";
import HBList from "../HBList";
import HBMessage from "../HBMessage";
import HBModal from "../HBModal";
import ModalWrap from "../HBModal/ModalWrap";
import HBConfirm from "../HBModal/confirm";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";

const scrollableTarget = "followerModalList";

const infiniteScrollOptions = {
  loader: null,
};

function FollowerCard({ record, onBlock }) {
  const user = useAppStore((store) => store.user);
  const { login } = useLogin();
  const [following, setFollowing] = useState(record.following);
  const { runAsync: runFollow } = useRequest(followUser, {
    manual: true,
  });
  const { runAsync: runUnfollow } = useRequest(unfollowUser, {
    manual: true,
  });

  const onToggleFollow = async () => {
    if (!user) {
      const result = await login({
        source_location: "关注者列表-关注",
      });
      if (!result) {
        return;
      }
    }

    if (following) {
      const result = await runUnfollow(record.urlname);
      if (!result.err) {
        setFollowing(false);
      }
    } else {
      const result = await runFollow(record.urlname);
      if (!result.err) {
        setFollowing(true);
      }
    }
  };

  const onBlockClick = useCallback(async () => {
    HBConfirm.open({
      title: "提示",
      content: `加入黑名单，对方无法再关注你，并且无法给你发私信。可在「个人设置」中管理黑名单`,
      okText: "确定",
      onOk: async () => {
        const res = await toBlackList({
          item_type: 3,
          item_value: record.user_id,
        });

        if (res.err) return;
        HBMessage.success("操作成功");
        onBlock && onBlock(record);
      },
    });
  }, [onBlock, record]);

  return (
    <div className={styles.follower}>
      <a
        href={`/user/${record.urlname}`}
        target="_blank"
        className={styles.avatar}
      >
        <img
          src={record.avatar_url || imageUrl(record.avatar)}
          alt={record.username}
        />
      </a>
      <div className={styles.info}>
        <a
          href={`/user/${record.urlname}`}
          target="_blank"
          className={styles.title}
        >
          {record.username}
        </a>
        <div className={styles.subtitle}>
          {displayNum(record.pin_count)}采集 ·{" "}
          {displayNum(record.follower_count)}粉丝
          {record.extra?.is_museuser ? "，认证设计师" : ""}
          {record.profile?.data?.location
            ? `，${record.profile?.data?.location}`
            : ""}
        </div>
      </div>
      <div className={styles.operation}>
        <When condition={record.user_id !== user?.user_id}>
          <If condition={following}>
            <Then>
              <If condition={record.following_me}>
                <Then>
                  <Button
                    className={styles.button}
                    size="small"
                    type="primary"
                    onClick={onToggleFollow}
                    shape="round"
                  >
                    互相关注
                  </Button>
                </Then>
                <Else>
                  <Button
                    className={styles.button}
                    size="small"
                    onClick={onToggleFollow}
                    type="primary"
                    shape="round"
                  >
                    已关注
                  </Button>
                </Else>
              </If>
            </Then>
            <Else>
              <HBButton
                className={styles.button}
                size="small"
                onClick={onToggleFollow}
                theme="gray"
              >
                关注
              </HBButton>
            </Else>
          </If>
        </When>
        <Dropdown
          placement="bottomRight"
          align={{ offset: [0, 12] }}
          overlay={
            <div className={styles.menu}>
              <a
                className={styles.menuItem}
                onClick={onBlockClick}
                data-gd-click="button_click"
                data-button-name="加入黑名单"
                data-report-type="用户"
              >
                <span className={styles.text}>加入黑名单</span>
              </a>
            </div>
          }
        >
          <div className={styles.more}>
            <HBIcon type="ic_more" />
          </div>
        </Dropdown>
      </div>
    </div>
  );
}

function FollowerList({ urlname }) {
  const [query, setQuery] = useSetState({
    max: 0,
    urlname: "",
  });
  const [hasMore, setHasMore] = useState(true);
  const [count, setCount] = useState(0);
  const [dataSource, setDataSource] = useState([]);
  useRequest(
    async () => {
      const { urlname, max } = query;

      if (!urlname) {
        return;
      }

      const result = await getFollowerUsers({
        urlname,
        limit: 20,
        ...(max ? { max } : null),
      });

      const users = result?.users || [];

      if (result.user?.follower_count) {
        setCount(result.user?.follower_count);
      }

      if (users.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(users));
    },
    {
      refreshDeps: [query],
    }
  );

  useEffect(() => {
    if (urlname) {
      setHasMore(true);
      setDataSource([]);
      setQuery({
        max: 0,
        urlname,
      });
    }
  }, [urlname, setQuery]);

  const onLoadMore = () => {
    if (dataSource.length > 0) {
      const last = dataSource[dataSource.length - 1];
      setQuery({
        max: last.seq || last.board_id,
      });
    }
  };

  const render = useCallback(
    (record) => (
      <FollowerCard
        record={record}
        key={record.user_id}
        onBlock={() => {
          setDataSource(
            dataSource.filter((item) => item.user_id !== record.user_id)
          );
          setCount(count - 1);
        }}
      />
    ),
    [count, dataSource]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.num}>{count}个粉丝</div>
      </div>
      <div className={styles.list} id={scrollableTarget}>
        <HBList
          scrollableTarget={scrollableTarget}
          infiniteScrollOptions={infiniteScrollOptions}
          disableMasonry={true}
          render={render}
          dataSource={dataSource}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
        />
      </div>
    </div>
  );
}

function FollowerModal({ urlname, ...props }) {
  return (
    <HBModal width={720} footer={null} wrapClassName={styles.modal} {...props}>
      <FollowerList urlname={urlname} />
    </HBModal>
  );
}

export default FollowerModal;
export const FollowerDialog = ModalWrap(FollowerModal);
